import type { Navigate } from './setup-surface.js';
import { renderCollection, type CollectionRow } from './collection-surface.js';

/**
 * CAOS-314 — the Setup app's PERMISSION SETS surface (the security-shape browse).
 *
 * Object Manager / App Manager / Value Sets browse the metadata catalog; this surface browses the
 * org's SECURITY shape, translated into CAOS metadata + the CAOS design system, matching the original
 * Dragon OS Setup → Permission Sets (list → a set's object/field grants + assignments → an explain-
 * access view). It is READ-ONLY for the set/group DEFINITION itself: a faithful browse of a
 * security-sensitive surface, never a live mutation of permission metadata.
 *
 * CAOS-1835 — "grant/revoke stay coming-soon" used to be the end of that sentence, and it is no
 * longer true of the PLATFORM, only of this screen. `metadata/create-permission-set`,
 * `update-permission-set`, `set-object-grant` and `set-field-grant` author a set and its grants
 * through the same deploy primitive every other authored change uses, gated on `metadata.author`.
 * What is still missing is a control HERE that calls them, because the `page` metatype declares no
 * actions at all — see {@link GRANT_EDIT_NOTE}, which is what the screen now says instead of
 * leaving a reader to look for a button that is not there.
 *
 * ASSIGNMENT is live — CAOS-310/CAOS-337 wired `permissions assign`/`unassign`
 * (CAOS-505/CAOS-511) into the Setup → Users detail page's `assign-set`/`assign-group`/`unassign-set`/
 * `unassign-group` actions, so a set or group is assigned/unassigned from the PRINCIPAL's own page;
 * this browse links straight to each assignee for that.
 *
 * It reads the kernel's `permissions` inspection noun (api/permissions-routes.ts) — which projects the
 * SAME derived grant/assignment tables + live `effective_*_perm` resolver views the access engine
 * enforces — so the browse can never diverge from what is actually enforced:
 *   • list    → GET /api/permissions/list                 (sets + groups + assignable principals)
 *   • detail  → GET /api/permissions/get?key=…            (one set/group's grants + assignees)
 *   • resolve → GET /api/permissions/resolve?subject=…    (a chosen user's RESOLVED effective access)
 *
 * The resolve view is the founder priority: it shows what a user can ACTUALLY do after every assigned
 * set/group combines (the union-minus-mute the kernel computes), never per-set defaults.
 *
 * Pure DOM + `--caos-*` tokens, mounted full-width inside the Setup console (inherits `.setup-pane`).
 * The list/detail split is route-driven (`/setup/permission-sets` and `/setup/permission-sets/<key>`),
 * exactly like Object Manager / Value Sets.
 */

// ── Projected shapes (mirror the kernel `permissions` noun payloads) ──────────────────────────────

export interface PrincipalSummary {
  id: string;
  login: string;
  status: string;
}

export interface ObjectGrant {
  object: string;
  read: boolean;
  create: boolean;
  edit: boolean;
  delete: boolean;
  viewAll: boolean;
  modifyAll: boolean;
}
export interface FieldGrant {
  field: string;
  read: boolean;
  edit: boolean;
}
/** CAOS-1840 — one app this set lets its holders ADMINISTER. Not "open": see `renderSetDetail`. */
export interface AppGrant {
  app: string;
  administer: boolean;
}
export interface PermissionSetDetail {
  key: string;
  label: string;
  kind: 'set';
  sessionRequired: boolean;
  objectGrants: ObjectGrant[];
  fieldGrants: FieldGrant[];
  systemGrants: string[];
  /** CAOS-1840 — optional on the wire, because a kernel older than this change sends none. */
  appGrants?: AppGrant[];
  /**
   * CAOS-1840 — the apps this set DECLARES visible. A separate field because it is a separate claim
   * and a separate source: `appGrants` comes from the control plane the access engine enforces,
   * this comes from the metadata, and nothing in the platform reads it. Also optional on the wire.
   */
  appVisibility?: string[];
  assignees: PrincipalSummary[];
}
export interface PermissionSetGroupDetail {
  key: string;
  label: string;
  kind: 'group';
  memberSets: { key: string; label: string }[];
  muteObjects: ObjectGrant[];
  muteFields: FieldGrant[];
  muteSystem: string[];
  assignees: PrincipalSummary[];
}

// ── The fetch seam (production → the host `/api/*` proxy; tests override it) ───────────────────────

export interface MetaResponse {
  json(): Promise<unknown>;
}
export type MetaFetch = (
  path: string,
  init?: { method?: string; headers?: Record<string, string>; body?: string },
) => Promise<MetaResponse>;

export const defaultMetaFetch: MetaFetch = (path, init) =>
  fetch(path, { credentials: 'same-origin', ...(init ?? {}) }) as unknown as Promise<MetaResponse>;

let metaFetch: MetaFetch = defaultMetaFetch;

/** Override the permissions fetch seam (production wiring / deterministic tests). */
export function setPermissionsFetch(fn: MetaFetch): void {
  metaFetch = fn;
}

interface Envelope {
  ok?: boolean;
  data?: unknown;
  error?: { message?: string } | undefined;
}

/** GET one `/api/permissions/*` route, returning its `data` payload or null (fail-soft). */
async function getData<T>(path: string): Promise<T | null> {
  let env: Envelope;
  try {
    const res = await metaFetch(path, { method: 'GET' });
    env = (await res.json()) as Envelope;
  } catch {
    return null;
  }
  if (env?.ok !== true) return null;
  return (env.data ?? null) as T | null;
}

// ── Small DOM helpers (mirror object-manager) ─────────────────────────────────────────────────────

function link(text: string, href: string, navigate?: Navigate): HTMLAnchorElement {
  const a = document.createElement('a');
  a.className = 'setup-link';
  a.href = href;
  a.textContent = text;
  a.addEventListener('click', (ev) => {
    if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
    ev.preventDefault();
    navigate?.(href);
  });
  return a;
}

function tag(text: string): HTMLElement {
  const el = document.createElement('caos-tag');
  el.textContent = text;
  return el;
}

/**
 * A yes/no permission cell — a check or a dash, never an ambiguous blank.
 *
 * CAOS-143 — `sense` exists because this same grid is drawn for two OPPOSITE meanings. In an
 * additive grant table a `✓` means the permission is delivered; in the Muting Set tables below it
 * means the permission is TAKEN AWAY. Sighted readers get that from the heading above the table,
 * but the accessible name is attached to the cell and travels without it — so until now a screen
 * reader announced every muted permission as "Granted", which is precisely backwards on the one
 * screen where being wrong about a subtraction matters.
 */
function grantCell(on: boolean, sense: 'grant' | 'mute' = 'grant'): HTMLElement {
  const el = document.createElement('span');
  el.className = on ? 'perm-yes' : 'perm-no';
  el.textContent = on ? '✓' : '—';
  const label = sense === 'mute' ? (on ? 'Removed' : 'Not removed') : on ? 'Granted' : 'Not granted';
  el.setAttribute('aria-label', label);
  return el;
}

/** A principal status pill (active → success, otherwise muted). */
function statusPill(status: string): HTMLElement {
  const pill = document.createElement('caos-status-pill');
  pill.setAttribute('intent', status === 'active' ? 'success' : 'muted');
  pill.textContent = status;
  return pill;
}

function kvRow(label: string, value: string): HTMLElement {
  const row = document.createElement('caos-key-value-row');
  row.setAttribute('label', label);
  row.setAttribute('stacked', '');
  row.setAttribute('no-border', '');
  if (value) row.setAttribute('value', value);
  return row;
}

function table(
  columns: { key: string; label: string; align?: string }[],
  rows: { id: string; cells: Record<string, unknown> }[],
  label: string,
  emptyLabel: string,
): HTMLElement {
  const t = document.createElement('caos-data-table');
  t.setAttribute('label', label);
  t.setAttribute('empty-label', emptyLabel);
  (t as unknown as { columns: unknown }).columns = columns;
  (t as unknown as { rows: unknown }).rows = rows;
  return t;
}

/**
 * CAOS-1835, AC4 — what these two tables cannot do, said ON the tables rather than left to be
 * discovered by looking for a button that is not there.
 *
 * The sentence is deliberately specific about WHICH half is missing, because the honest answer
 * changed with this ticket and a vaguer one would now be wrong in the other direction. Editing a
 * grant IS possible: `metadata/set-object-grant` and `metadata/set-field-grant` do it through the
 * same deploy primitive every other authored change goes through, gated on `metadata.author`. What
 * is missing is the control on this screen that calls them — the `page` metatype declares no
 * actions at all, so a create or edit affordance here needs the page grammar to grow one, and that
 * is not something a package can author today.
 *
 * So the disclosure names the capability and its absence from the screen separately. "Coming soon"
 * would be the wrong shape twice over: it implies nobody can do this yet, which is no longer true,
 * and it gives a reader nothing to act on. A reader who needs to change a grant today can.
 */
const GRANT_EDIT_NOTE =
  'This is a read-only view of what the set grants. Grants are changed by deploying the permission ' +
  'set — through the CLI, the editor extension, or the platform’s own authoring verbs — and this ' +
  'screen does not yet have a control that does it for you.';

function note(text: string): HTMLElement {
  const p = document.createElement('p');
  p.className = 'setup-note';
  p.textContent = text;
  return p;
}

function breadcrumb(label: string, navigate?: Navigate): HTMLElement {
  const crumbs = document.createElement('caos-breadcrumb');
  (crumbs as unknown as { items: unknown }).items = [
    { label: 'Permission Sets', href: '/setup/permission-sets' },
    { label },
  ];
  crumbs.addEventListener('click', (ev) => {
    const anchor = (ev.target as HTMLElement | null)?.closest('a');
    const href = anchor?.getAttribute('href');
    if (href && href.startsWith('/')) {
      ev.preventDefault();
      navigate?.(href);
    }
  });
  return crumbs;
}

const OBJECT_PERM_COLUMNS = [
  { key: 'object', label: 'Object' },
  { key: 'read', label: 'Read', align: 'center' },
  { key: 'create', label: 'Create', align: 'center' },
  { key: 'edit', label: 'Edit', align: 'center' },
  { key: 'delete', label: 'Delete', align: 'center' },
  { key: 'viewAll', label: 'View All', align: 'center' },
  { key: 'modifyAll', label: 'Modify All', align: 'center' },
];

function objectPermRows(grants: ObjectGrant[], sense: 'grant' | 'mute' = 'grant'): { id: string; cells: Record<string, unknown> }[] {
  return grants.map((g) => ({
    id: g.object,
    cells: {
      object: g.object,
      read: { node: grantCell(g.read, sense) },
      create: { node: grantCell(g.create, sense) },
      edit: { node: grantCell(g.edit, sense) },
      delete: { node: grantCell(g.delete, sense) },
      viewAll: { node: grantCell(g.viewAll, sense) },
      modifyAll: { node: grantCell(g.modifyAll, sense) },
    },
  }));
}

// ── List view ─────────────────────────────────────────────────────────────────────────────────

// ── Detail views ────────────────────────────────────────────────────────────────────────────────

/** Render one permission set's detail: identity, object grants, FLS, system grants, assignees. */
function renderSetDetail(
  root: HTMLElement,
  detail: PermissionSetDetail,
  navigate?: Navigate,
  pathname?: string,
): void {
  root.replaceChildren();
  root.append(breadcrumb(detail.label, navigate));

  const head = document.createElement('caos-record-header');
  head.setAttribute('object-label', 'Permission Set');
  head.setAttribute('heading', detail.label);
  head.setAttribute('no-icon', '');
  root.append(head);

  // Details.
  const details = document.createElement('caos-detail-section');
  details.setAttribute('heading', 'Details');
  const grid = document.createElement('div');
  grid.className = 'setup-field-grid';
  grid.append(
    kvRow('Label', detail.label),
    kvRow('API Name', detail.key),
    kvRow('Type', 'Permission Set'),
    kvRow('Session required', detail.sessionRequired ? 'Yes' : 'No'),
    kvRow('Object grants', String(detail.objectGrants.length)),
    kvRow('Field grants', String(detail.fieldGrants.length)),
  );
  details.append(grid);
  root.append(details);

  // Object permissions.
  const objSection = document.createElement('caos-detail-section');
  objSection.setAttribute('heading', 'Object Permissions');
  if (detail.objectGrants.length === 0) {
    const empty = document.createElement('caos-empty-state');
    empty.setAttribute('title', 'No object permissions');
    empty.textContent = 'This set grants no object-level access.';
    objSection.append(empty);
  } else {
    objSection.append(table(OBJECT_PERM_COLUMNS, objectPermRows(detail.objectGrants), 'Object permissions', 'No object permissions.'));
  }
  objSection.append(note(GRANT_EDIT_NOTE));
  root.append(objSection);

  // Field-level security.
  const fldSection = document.createElement('caos-detail-section');
  fldSection.setAttribute('heading', 'Field-Level Security');
  if (detail.fieldGrants.length === 0) {
    const empty = document.createElement('caos-empty-state');
    empty.setAttribute('title', 'No field-level security');
    empty.textContent = 'This set grants no field-level access.';
    fldSection.append(empty);
  } else {
    fldSection.append(
      table(
        [
          { key: 'field', label: 'Field' },
          { key: 'read', label: 'Read', align: 'center' },
          { key: 'edit', label: 'Edit', align: 'center' },
        ],
        detail.fieldGrants.map((f) => ({
          id: f.field,
          cells: { field: f.field, read: { node: grantCell(f.read) }, edit: { node: grantCell(f.edit) } },
        })),
        'Field-level security',
        'No field-level security.',
      ),
    );
  }
  fldSection.append(note(GRANT_EDIT_NOTE));
  root.append(fldSection);

  // System permissions.
  if (detail.systemGrants.length > 0) {
    const sysSection = document.createElement('caos-detail-section');
    sysSection.setAttribute('heading', 'System Permissions');
    const chips = document.createElement('div');
    chips.className = 'perm-chip-row';
    for (const p of detail.systemGrants) chips.append(tag(p));
    sysSection.append(chips);
    root.append(sysSection);
  }

  root.append(renderAppGrants(detail.appGrants, detail.appVisibility));
  root.append(renderAssignees(detail.assignees, navigate));
}

/** Render one permission-set group's detail: member sets, muting set, assignees. */
function renderGroupDetail(
  root: HTMLElement,
  detail: PermissionSetGroupDetail,
  navigate?: Navigate,
  pathname?: string,
): void {
  root.replaceChildren();
  root.append(breadcrumb(detail.label, navigate));

  const head = document.createElement('caos-record-header');
  head.setAttribute('object-label', 'Permission Set Group');
  head.setAttribute('heading', detail.label);
  head.setAttribute('no-icon', '');
  root.append(head);

  const details = document.createElement('caos-detail-section');
  details.setAttribute('heading', 'Details');
  const grid = document.createElement('div');
  grid.className = 'setup-field-grid';
  grid.append(
    kvRow('Label', detail.label),
    kvRow('API Name', detail.key),
    kvRow('Type', 'Permission Set Group'),
    kvRow('Member sets', String(detail.memberSets.length)),
  );
  details.append(grid);
  root.append(details);

  // Member sets — each links into its own set detail.
  const membersSection = document.createElement('caos-detail-section');
  membersSection.setAttribute('heading', 'Member Sets');
  if (detail.memberSets.length === 0) {
    const empty = document.createElement('caos-empty-state');
    empty.setAttribute('title', 'No member sets');
    empty.textContent = 'This group unions no permission sets.';
    membersSection.append(empty);
  } else {
    membersSection.append(
      table(
        [
          { key: 'label', label: 'Set' },
          { key: 'api', label: 'API Name' },
        ],
        detail.memberSets.map((s) => ({
          id: s.key,
          cells: { label: { node: link(s.label, `/setup/permission-sets/${s.key}`, navigate) }, api: s.key },
        })),
        'Member sets',
        'No member sets.',
      ),
    );
  }
  root.append(membersSection);

  // The one muting set — subtractions this group applies to its members' grants.
  const muteSection = document.createElement('caos-detail-section');
  muteSection.setAttribute('heading', 'Muting Set');
  const hasMute = detail.muteObjects.length > 0 || detail.muteFields.length > 0 || detail.muteSystem.length > 0;
  if (!hasMute) {
    const empty = document.createElement('caos-empty-state');
    empty.setAttribute('title', 'No muting set');
    empty.textContent = 'This group subtracts nothing from its member sets.';
    muteSection.append(empty);
  } else {
    if (detail.muteObjects.length > 0) {
      muteSection.append(table(OBJECT_PERM_COLUMNS, objectPermRows(detail.muteObjects, 'mute'), 'Muted object permissions', ''));
    }
    if (detail.muteFields.length > 0) {
      muteSection.append(
        table(
          [
            { key: 'field', label: 'Field' },
            { key: 'read', label: 'Mute Read', align: 'center' },
            { key: 'edit', label: 'Mute Edit', align: 'center' },
          ],
          detail.muteFields.map((f) => ({
            id: f.field,
            cells: { field: f.field, read: { node: grantCell(f.read, 'mute') }, edit: { node: grantCell(f.edit, 'mute') } },
          })),
          'Muted field-level security',
          '',
        ),
      );
    }
    if (detail.muteSystem.length > 0) {
      const chips = document.createElement('div');
      chips.className = 'perm-chip-row';
      for (const p of detail.muteSystem) chips.append(tag(p));
      muteSection.append(chips);
    }
    muteSection.append(note('A muting set removes only permissions delivered by THIS group’s member sets.'));
  }
  root.append(muteSection);

  root.append(renderAssignees(detail.assignees, navigate));
}

/**
 * CAOS-1840 — the "Apps" section: BOTH things this set says about an app, kept apart.
 *
 * A permission set makes two declarations about an app and they are not the same claim:
 *
 *   • `appPermissions[{ app, administer }]` — ENFORCED. Projected into `caos.perm_app_grant` and
 *     read by one gate: the account menu's per-app Settings entry.
 *   • `appVisibility[]` — DECLARED AND INERT, and it exists only in the metadata; nothing projects
 *     it and nothing reads it.
 *
 * The two are drawn as two columns rather than folded into one "has access" mark, because folding
 * them would report an inert declaration as a grant — and a set that declares visibility and grants
 * nothing is exactly the shape an org that believes it restricted its apps has authored.
 *
 * ── WHY THIS SECTION SAYS WHAT IT DOES NOT COVER, which is most of its value ─────────────────────
 * Object Permissions, Field-Level Security and System Permissions each read a plane the access
 * engine enforces, so a reader can take each at face value. This one is different and the
 * difference cannot be left to be discovered.
 *
 * `perm_app_grant` carries one flag — `administer` — and exactly one gate reads it: the account
 * menu's per-app Settings entry. Whether an app can be OPENED is `appVisibility[]`, declared on the
 * same permission-set component and validated at deploy, and consumed by nothing: `meta/page.ts`
 * states it outright ("appVisibility and tabVisibility are declared, validated and projected, and
 * no reader consumes either"), and the host confirms it from the other end — `metadata-source.ts`
 * pushes every component of type `app` with no filter at all.
 *
 * So an administrator reading an Apps section that lists two apps would conclude the other apps are
 * closed to this set. That is false, and it is false in the dangerous direction: they would believe
 * access is restricted where it is not. The sentence in the empty state and the note beside the
 * table are therefore load-bearing rather than decoration, and this section is drawn even when
 * there are no grants — unlike System Permissions above it, which is hidden when empty, because an
 * absent section says nothing and here the nothing IS the finding.
 *
 * The grants it does show are real and enforced. The ticket this comes from (CAOS-1840) asked for
 * "which permission sets can open this app" and the honest answer is that every holder of any set
 * can open every installed app; enforcing the declaration is a separate change, with a default to
 * decide for the orgs that declare nothing.
 */
const APP_VISIBILITY_NOTE =
  'Administering an app is not the same as opening one. Every installed app is available to ' +
  'everyone who can reach this workspace — apps are not restricted by permission set, so this ' +
  'section does not say which apps a holder of this set can open. What it lists is which apps they ' +
  'can manage the settings of.';

function renderAppGrants(grants: AppGrant[] | undefined, declaresVisible: string[] | undefined): HTMLElement {
  const section = document.createElement('caos-detail-section');
  section.setAttribute('heading', 'Apps');

  // Presence-only on the enforced side: `caos.perm_app_grant` stores no row for `administer: false`,
  // so a row that arrives false is not a grant and must not be listed as one.
  const administered = new Set((grants ?? []).filter((g) => g.administer).map((g) => g.app));
  const declared = new Set(declaresVisible ?? []);
  // A set that says EITHER thing about an app earns that app a row. The union is sorted so the
  // section does not reorder between two reads of the same set.
  const apps = [...new Set([...administered, ...declared])].sort((a, b) => a.localeCompare(b));

  if (apps.length === 0) {
    const empty = document.createElement('caos-empty-state');
    empty.setAttribute('title', 'This set says nothing about any app');
    // Said in full here rather than split between a title and a note, because this is the state the
    // section exists for and the one a reader is most likely to misread.
    empty.textContent =
      'Nobody holding this set can manage an app’s settings through it, and it declares no app visible. ' +
      APP_VISIBILITY_NOTE;
    section.append(empty);
    return section;
  }

  section.append(
    table(
      [
        { key: 'app', label: 'App' },
        { key: 'administer', label: 'Can manage its settings', align: 'center' },
        { key: 'visible', label: 'Declares it visible', align: 'center' },
      ],
      apps.map((app) => ({
        id: app,
        cells: {
          app,
          administer: { node: grantCell(administered.has(app)) },
          visible: { node: grantCell(declared.has(app)) },
        },
      })),
      'Apps',
      'No apps.',
    ),
  );
  const note = document.createElement('p');
  note.className = 'perm-note';
  note.textContent = APP_VISIBILITY_NOTE;
  section.append(note);
  return section;
}

/**
 * The "Assigned Users" section — who holds this set/group. Each row links to that person's own Setup →
 * Users page (CAOS-310) — the place to assign/unassign this set or group for them, since that page is
 * the honest owner of the assignment action, not this read-only browse of the set/group itself.
 */
function renderAssignees(assignees: PrincipalSummary[], navigate?: Navigate): HTMLElement {
  const section = document.createElement('caos-detail-section');
  section.setAttribute('heading', 'Assigned Users');
  if (assignees.length === 0) {
    const empty = document.createElement('caos-empty-state');
    empty.setAttribute('title', 'No assigned users');
    empty.textContent = 'No users are assigned this yet.';
    section.append(empty);
    return section;
  }
  section.append(
    table(
      [
        { key: 'login', label: 'User' },
        { key: 'status', label: 'Status' },
      ],
      assignees.map((a) => ({
        id: a.id,
        cells: { login: { node: link(a.login, `/setup/users/${a.id}`, navigate) }, status: { node: statusPill(a.status) } },
      })),
      'Assigned users',
      'No assigned users.',
    ),
  );
  section.append(note('Assign or unassign this set/group for a user from their own Setup → Users page — click their name above.'));
  return section;
}

/** An honest not-found pane for an unknown permission-set/group key. */
function renderNotFound(root: HTMLElement, key: string, navigate?: Navigate): void {
  root.replaceChildren();
  root.append(breadcrumb(key, navigate));
  const empty = document.createElement('caos-empty-state');
  empty.setAttribute('title', 'Permission set not found');
  empty.textContent = `No permission set or group named "${key}" is defined in this org.`;
  root.append(empty);
}

// ── Entry point ───────────────────────────────────────────────────────────────────────────────

export interface PermissionSetsContext {
  readonly pathname: string;
  readonly navigate?: Navigate;
}

/** Parse `/setup/permission-sets/<key>` → the detail key, absent on the browse route. */
function detailKey(pathname: string): string | undefined {
  const segments = pathname.split('/').filter(Boolean); // ['setup', 'permission-sets', <key>]
  return segments[2];
}

/**
 * Render one permission set's or group's DETAIL. Paints a loading container immediately, then
 * fetches that key. Returns `{ root, done }` so a test can await the read before asserting.
 *
 * CAOS-395 — THIS NO LONGER DRAWS THE BROWSE, and the keyless path is a refusal rather than a
 * fallback. `caos-pkg-setup` ships `page:permission_sets` — a `list` dataSource over the
 * `permission_set` system object — and `setup-surface.ts` now dispatches here only when a key is
 * present, so the bare route reaches the authored page instead. Rendering a second browse here
 * would put two implementations of one screen in the tree, which is the condition this ticket
 * exists to end.
 *
 * It says so out loud instead of painting an empty state, because the two failures look identical
 * from the outside: an org whose permission sets genuinely cannot be read, and a dispatch that
 * regressed and sent the browse back here. An empty state would make the second one invisible.
 */
export function renderPermissionSets(ctx: PermissionSetsContext): { root: HTMLElement; done: Promise<void> } {
  const root = document.createElement('div');
  root.className = 'setup-pane';
  const loading = document.createElement('caos-empty-state');
  loading.setAttribute('title', 'Loading…');
  root.append(loading);

  const key = detailKey(ctx.pathname);

  const done = (async (): Promise<void> => {
    if (!key) {
      root.replaceChildren(
        breadcrumb('Permission Sets', ctx.navigate),
        note(
          'The Permission Sets browse is drawn by the authored page that ships with Setup, not by ' +
            'this surface. Reaching this message means the route was dispatched here without a ' +
            'permission set key.',
        ),
      );
      return;
    }
    const detail = await getData<PermissionSetDetail | PermissionSetGroupDetail>(
      `/api/permissions/get?key=${encodeURIComponent(key)}`,
    );
    if (!detail) {
      renderNotFound(root, key, ctx.navigate);
      return;
    }
    if (detail.kind === 'group') renderGroupDetail(root, detail, ctx.navigate, ctx.pathname);
    else renderSetDetail(root, detail as PermissionSetDetail, ctx.navigate, ctx.pathname);
  })();

  return { root, done };
}
